import {ThemeEvents} from "@theme/events";

/**
 * Nova Drawer Content - keeps drawer text (product/variant metafield liquid)
 * in sync when the customer switches variants.
 *
 * Drawer content is rendered server-side from `block.settings.content`
 * liquid strings (e.g. a variant metaobject reference), so on variant change
 * we pull the refreshed markup out of the section-rendering HTML rather than
 * re-evaluating anything client-side (same pattern as nova-price-pdp / product-description-metafields).
 */
class NovaDrawerContent extends HTMLElement {
	/** @type {((event: Event) => void) | null} */
	#onVariantUpdate = null;

	connectedCallback() {
		this.#onVariantUpdate = this.updateContent.bind(this);
		document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
	}

	disconnectedCallback() {
		if (this.#onVariantUpdate) {
			document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
			this.#onVariantUpdate = null;
		}
	}

	updateContent = event => {
		const newHtml = event.detail?.data?.html;
		if (!newHtml) return;

		const drawerContent = this.querySelector("[data-drawer-content]");
		const currentText = this.querySelector('[ref="drawerText"]');
		if (!drawerContent || !currentText || !drawerContent.id) return;

		const newText = newHtml.querySelector(`#${CSS.escape(drawerContent.id)} [ref="drawerText"]`);
		if (!newText) return;

		if (newText.innerHTML === currentText.innerHTML) return;

		currentText.innerHTML = newText.innerHTML;

		// Recalculate the open max-height if the drawer is currently expanded,
		// since the transition relies on an inline px value set at toggle time.
		if (drawerContent.getAttribute("aria-hidden") === "false") {
			drawerContent.style.maxHeight = `${drawerContent.scrollHeight}px`;
		}
	};
}

if (!customElements.get("nova-drawer-content")) {
	customElements.define("nova-drawer-content", NovaDrawerContent);
}

/**
 * Single delegated listener for every drawer trigger on the page.
 *
 * Each nova-drawer* block used to ship its own copy of this toggle script,
 * scanning the whole document for `.nova-drawer` on DOMContentLoaded. When more
 * than one drawer block rendered on the same page, every copy attached its own
 * click listener to every trigger, so a single click fired the toggle multiple
 * times (an even count silently cancelled out, making the drawer look broken).
 * This module only runs once per page (native ES module de-duplication), so
 * delegating from here guarantees exactly one listener regardless of how many
 * drawer block types are present.
 */
document.addEventListener("click", event => {
	if (!(event.target instanceof Element)) return;

	const trigger = event.target.closest("[data-drawer-trigger]");
	if (!trigger) return;

	const content = trigger.closest(".nova-drawer")?.querySelector("[data-drawer-content]");
	if (!content) return;

	const isExpanded = trigger.getAttribute("aria-expanded") === "true";

	trigger.setAttribute("aria-expanded", String(!isExpanded));
	content.setAttribute("aria-hidden", String(isExpanded));
	content.style.maxHeight = isExpanded ? "0" : `${content.scrollHeight}px`;
});
